import { createInertiaApp, router } from '@inertiajs/react';
import NProgress from 'nprogress';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import '../css/fonts.css';
import '../css/subscription.css';
import ErrorBoundary from './Components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { runMetaParamBuilder } from './metaParamBuilder';
import { initFacebookPixel } from './pixel';

const scheduleIdleTask = (callback: () => void) => {
  if (typeof window === 'undefined') {
    return;
  }

  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(callback, { timeout: 1600 });
    return;
  }

  window.setTimeout(callback, 600);
};

// NProgress hooks
if (typeof window !== 'undefined') {
  // Facebook Pixel must fire immediately for accurate tracking/CAPI
  initFacebookPixel(router);

  // Defer non-critical scripts until after LCP
  scheduleIdleTask(() => {
    void runMetaParamBuilder();
  });

  scheduleIdleTask(() => {
    import('bootstrap/dist/js/bootstrap.bundle.min.js')
      .catch(() => {
        // silently ignore if bootstrap fails to load; components will gracefully degrade
      });
  });

  router.on('start', () => NProgress.start());
  router.on('progress', (e) => {
    const p = e.detail.progress?.percentage;
    if (p !== undefined) NProgress.set((p / 100) * 0.9);
  });
  router.on('finish', (e) => {
    if (!NProgress.isStarted()) return;
    if (e.detail.visit.completed || e.detail.visit.cancelled) {
      NProgress.done();
      if (e.detail.visit.cancelled) NProgress.remove();
      void runMetaParamBuilder();
    } else if (e.detail.visit.interrupted) {
      NProgress.set(0);
    }
  });
}

createInertiaApp({
  resolve: async (name: string) => {
    const pages = import.meta.glob('./Pages/**/*.tsx') as Record<string, () => Promise<{ default: React.ComponentType<any> }>>;
    const importPage = pages[`./Pages/${name}.tsx`];
    if (!importPage) throw new Error(`Page not found: ${name}`);

    const mod = await importPage();
    type PageComponent = React.ComponentType<any> & { layout?: (page: React.ReactNode) => React.ReactNode };
    const page = mod.default as PageComponent;
    page.layout ??= (page: React.ReactNode) => <>{page}</>;
    return page;
  },
  setup({ el, App, props }) {
    hydrateRoot(el, (
      <ThemeProvider>
        <ErrorBoundary>
          <App {...props} />
        </ErrorBoundary>
      </ThemeProvider>
    ));
  },
  progress: false, // kita pakai NProgress sendiri
});
